import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../ui/dialog";
import Button from "../../button-v1";
import { parseSegmentsWithDateTags } from "../../../utils/parseBoldMarkers";
import {
  contentEditableToMarkerString,
  renderMarkerStringIntoContentEditable,
  setCaretAtDisplayOffset,
  markerStringDisplayLength,
  insertTypedTextAsPlain,
  wouldEmptyRow,
  caretDisplayOffset,
  isCaretOnEdgeLine,
} from "../../../utils/contentEditableMarkers";
import DeltaDateTag from "./DeltaDateTag";
import styles from "./structuredListSection.module.css";

// SCRUM-941: numbered/bulleted list editor for the care plan sections that
// read best as separate points (Goals, Interventions, Medications,
// Recommendations, Potential Barriers). SCRUM-738 had swapped the old
// one-textarea-per-line NumberedTextArea for FreeTextSection because users
// could only delete one point at a time; this keeps the one-row-per-point
// layout with a hover ✕ to remove a single point and the top-right ✕ to
// clear the whole section.
//
// Storage is unchanged - the same "\n"-joined string FreeTextSection used,
// one line per item, with "**bold**" AI markers kept intact (they never span
// a line break, see parseBoldMarkers) - so saving, AI fill, copy-forward and
// the PDFs keep reading these fields exactly as before.

const toItems = (value) => (value ? value.split("\n") : [""]);

// An all-blank list is an empty field, same as FreeTextSection reporting ""
// for a field with no visible text (saves [] rather than [""]).
const toValue = (items) => (items.some((item) => item.trim()) ? items.join("\n") : "");

const ItemText = ({ text }) =>
  parseSegmentsWithDateTags(text).map((segment, i) =>
    segment.bold ? (
      <strong key={i} style={{ fontWeight: 700, color: "#6b7280" }}>
        {segment.text}
        <DeltaDateTag dateTag={segment.dateTag} />
      </strong>
    ) : (
      <React.Fragment key={i}>{segment.text}</React.Fragment>
    )
  );

// One editable point - the same contentEditable row as SymptomNameEditor
// (real bold, native caret), with Enter splitting into a new point,
// Backspace at the start merging into the previous one, Up/Down moving
// between points, and a multi-line paste becoming one point per line.
const ListItemEditor = ({
  value,
  placeholder,
  showPlaceholder,
  blockInsert,
  onChange,
  onSplit,
  onMergeUp,
  onPasteLines,
  onMove,
  onRemove,
  onRef,
}) => {
  const ref = useRef(null);

  // Compares against the DOM itself rather than a cached "last value" ref -
  // see FreeTextSection's identical sync effect for why.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (contentEditableToMarkerString(el) === (value || "")) return;
    renderMarkerStringIntoContentEditable(el, value);
  }, [value]);

  const handleInput = () => {
    const el = ref.current;
    if (!el) return;
    onChange(contentEditableToMarkerString(el));
  };

  // Typing right after (or inside) AI bold text stays plain - see
  // insertTypedTextAsPlain (native listener: React 17's onBeforeInput has no
  // inputType).
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onNativeBeforeInput = (e) => {
      if (blockInsert && e.inputType && e.inputType.startsWith("insert")) {
        e.preventDefault();
        return;
      }
      if (insertTypedTextAsPlain(e, el)) handleInput();
    };
    el.addEventListener("beforeinput", onNativeBeforeInput);
    return () => el.removeEventListener("beforeinput", onNativeBeforeInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, blockInsert]);

  // The item's text before and after the caret, as raw marker strings.
  const splitAtCaret = () => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    const beforeRange = document.createRange();
    beforeRange.selectNodeContents(el);
    beforeRange.setEnd(range.startContainer, range.startOffset);
    const afterRange = document.createRange();
    afterRange.selectNodeContents(el);
    afterRange.setStart(range.endContainer, range.endOffset);
    return {
      before: contentEditableToMarkerString(beforeRange.cloneContents()),
      after: contentEditableToMarkerString(afterRange.cloneContents()),
      collapsed: sel.isCollapsed,
    };
  };

  const handleKeyDown = (e) => {
    if (e.nativeEvent?.isComposing) return;
    const parts =
      ["Enter", "Backspace", "Delete", "ArrowUp", "ArrowDown"].includes(e.key) && splitAtCaret();
    if (!parts) return;

    if ((e.key === "Backspace" || e.key === "Delete") && wouldEmptyRow(e.key, parts)) {
      // Deleting the last character(s) removes the whole point - number and
      // all - instead of leaving an empty numbered row behind.
      e.preventDefault();
      onRemove();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (blockInsert) return;
      onSplit(parts.before, parts.after);
    } else if (e.key === "Backspace" && parts.collapsed && parts.before === "") {
      e.preventDefault();
      onMergeUp(parts.after);
    } else if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !e.shiftKey) {
      // From the point's first/last line, move to the point above/below,
      // keeping roughly the same position along the line.
      const direction = e.key === "ArrowUp" ? -1 : 1;
      if (isCaretOnEdgeLine(ref.current, direction) && onMove(direction, caretDisplayOffset(ref.current))) {
        e.preventDefault();
      }
    }
  };

  // Pasted text is inserted as plain text (no foreign HTML in the row); a
  // multi-line paste becomes one item per line.
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData("text/plain");
    if (text == null) return;
    e.preventDefault();
    if (blockInsert) return;
    const lines = text.replace(/\r\n?/g, "\n").split("\n");
    if (lines.length === 1) {
      document.execCommand("insertText", false, text);
      return;
    }
    const parts = splitAtCaret();
    if (parts) onPasteLines(parts.before, lines, parts.after);
  };

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      {showPlaceholder && <span className={styles.placeholder}>{placeholder}</span>}
      <div
        ref={(el) => {
          ref.current = el;
          onRef && onRef(el);
        }}
        className={styles.itemEditor}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
    </div>
  );
};

const Marker = ({ listStyle, index }) => (
  <span className={styles.marker}>{listStyle === "bulleted" ? "•" : `${index + 1}.`}</span>
);

const StructuredListSection = ({
  label,
  placeholder = "Enter an item...",
  value = "",
  onChange,
  readOnly = false,
  listStyle = "numbered",
  minHeight = 60,
  maxLength = 5000,
  showClear = true,
  headerExtra = null,
}) => {
  const items = toItems(value);
  const lineRefs = useRef([]);
  const pendingFocusRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useLayoutEffect(() => {
    if (readOnly || !pendingFocusRef.current) return;
    const { index, pos } = pendingFocusRef.current;
    pendingFocusRef.current = null;
    const el = lineRefs.current[index];
    if (!el) return;
    el.focus();
    // pos is a display-text offset (no "**" markers) - see
    // setCaretAtDisplayOffset.
    setCaretAtDisplayOffset(el, pos);
  }, [value, readOnly]);

  const length = (value || "").length;
  const nearOrOverLimit = maxLength && length >= maxLength * 0.9;
  const overLimit = maxLength && length > maxLength;
  // Same as FreeTextSection: blocks new growth at the cap but never clips
  // content that's already over it.
  const atMaxLength = maxLength && length >= maxLength;

  const emit = (nextItems) => onChange && onChange(toValue(nextItems));

  const focusItem = (index, pos) => {
    pendingFocusRef.current = { index, pos };
  };

  const updateItem = (index, text) => {
    const next = [...items];
    next[index] = text;
    emit(next);
  };

  const handleSplit = (index, before, after) => {
    const next = [...items];
    next.splice(index, 1, before, after);
    focusItem(index + 1, 0);
    // An all-blank list collapses to "" (a single row), so focus the row
    // that'll actually exist.
    if (!toValue(next)) focusItem(0, 0);
    emit(next);
  };

  const handleMergeUp = (index, rest) => {
    if (index === 0) return;
    const prev = items[index - 1];
    const next = [...items];
    next.splice(index - 1, 2, prev + rest);
    focusItem(index - 1, markerStringDisplayLength(prev));
    emit(next);
  };

  const handlePasteLines = (index, before, lines, after) => {
    const pasted = [...lines];
    pasted[0] = before + pasted[0];
    const last = pasted.length - 1;
    const caret = markerStringDisplayLength(pasted[last]);
    pasted[last] += after;
    const next = [...items];
    next.splice(index, 1, ...pasted);
    focusItem(index + last, caret);
    emit(next);
  };

  // A row emptied by Backspace/Delete goes away entirely; the caret moves to
  // the end of the row above (or the start of the new first row).
  const handleRemove = (index) => {
    const next = items.filter((_, i) => i !== index);
    if (!toValue(next)) focusItem(0, 0);
    else if (index > 0) focusItem(index - 1, markerStringDisplayLength(items[index - 1]));
    else focusItem(0, 0);
    emit(next.length ? next : [""]);
  };

  // Returns false when there's no row that way, so the key keeps its native
  // behavior.
  const handleMove = (index, direction, offset) => {
    const target = index + direction;
    const el = lineRefs.current[target];
    if (target < 0 || target >= items.length || !el) return false;
    el.focus();
    setCaretAtDisplayOffset(el, Math.min(offset, markerStringDisplayLength(items[target])));
    return true;
  };

  const addItem = () => {
    if (atMaxLength) return;
    const lastIndex = items.length - 1;
    // Reuse a trailing blank row instead of stacking empty ones.
    if (!items[lastIndex].trim()) {
      lineRefs.current[lastIndex]?.focus();
      return;
    }
    focusItem(items.length, 0);
    emit([...items, ""]);
  };

  const isEmpty = items.length === 1 && !items[0].trim();
  const hasContent = items.some((item) => item.trim());

  const handleClear = () => {
    onChange && onChange("");
    setConfirmOpen(false);
  };

  if (readOnly) {
    const filled = items.filter((item) => item.trim());
    const ListTag = listStyle === "bulleted" ? "ul" : "ol";
    return (
      <div className={styles.field}>
        {label && <label className={styles.readOnlyLabel}>{label}</label>}
        <div className={styles.readOnlyBox} style={{ minHeight: `${minHeight}px` }}>
          {filled.length ? (
            <ListTag className={styles.readOnlyList}>
              {filled.map((item, i) => (
                <li key={i} className={styles.row}>
                  <Marker listStyle={listStyle} index={i} />
                  <span className={styles.readOnlyText}>
                    <ItemText text={item} />
                  </span>
                </li>
              ))}
            </ListTag>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <div className={styles.header}>
        {label && <label className={styles.label}>{label}</label>}
        <div className={styles.headerActions}>
          {headerExtra}
          {showClear && hasContent && (
            <button
              type="button"
              className={styles.clearAll}
              onClick={() => setConfirmOpen(true)}
              title={`Clear ${label || "section"}`}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className={styles.box} style={{ minHeight: `${minHeight}px` }}>
        {items.map((item, i) => {
          // An empty field shows just the placeholder, lined up like every
          // other care plan field; the number appears with the first letter.
          // The `false` slot keeps ListItemEditor at the same child position,
          // so it isn't remounted (and keeps focus) when it does.
          return (
            <div key={i} className={styles.row}>
              {!isEmpty && <Marker listStyle={listStyle} index={i} />}
              <ListItemEditor
                value={item}
                placeholder={placeholder}
                showPlaceholder={isEmpty}
                blockInsert={atMaxLength}
                onChange={(text) => updateItem(i, text)}
                onSplit={(before, after) => handleSplit(i, before, after)}
                onMergeUp={(rest) => handleMergeUp(i, rest)}
                onPasteLines={(before, lines, after) => handlePasteLines(i, before, lines, after)}
                onMove={(direction, offset) => handleMove(i, direction, offset)}
                onRemove={() => handleRemove(i)}
                onRef={(el) => (lineRefs.current[i] = el)}
              />
              {items.length > 1 && (
                <button
                  type="button"
                  className={styles.removeItem}
                  onClick={() => emit(items.filter((_, j) => j !== i))}
                  title="Remove item"
                  aria-label={`Remove item ${i + 1}`}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
        {!isEmpty && (
          <button type="button" className={styles.addItem} onClick={addItem} disabled={atMaxLength}>
            + Add item
          </button>
        )}
      </div>

      {maxLength ? (
        <span
          className={styles.counter}
          style={{ color: overLimit ? "#ef4444" : nearOrOverLimit ? "#f59e0b" : "#94A3B8" }}
        >
          {length} / {maxLength}
        </span>
      ) : null}

      <Dialog open={confirmOpen} setOpen={setConfirmOpen}>
        <DialogContent style={{ maxWidth: "420px" }}>
          <DialogHeader>
            <DialogTitle>Clear {label || "Section"}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p style={{ color: "#565975", fontSize: "14px", lineHeight: 1.5, margin: 0 }}>
              All items in {label || "this section"} will be removed — the rest of
              the care plan is not affected. Are you sure you want to proceed?
            </p>
          </DialogBody>
          <DialogFooter style={{ justifyContent: "space-between", gap: "12px" }}>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              style={{ flex: 1, border: "1.5px solid #10164d", color: "#10164d", backgroundColor: "#ffffff" }}
            >
              No
            </Button>
            <Button variant="primary" onClick={handleClear} style={{ flex: 1 }}>
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StructuredListSection;
